/* ──────────────────────────────────────────────────────────────
   VacacionesProfesorPage.tsx
   – El profesor puede SOLICITAR / CANCELAR días de vacación
   – El profesor puede SOLICITAR ausencias con justificante
   – NUEVO: pestaña «Resumen» con recuento de vacaciones
   – Colores:
       · azul      → vacaciones aprobadas
       · amarillo  → vacaciones pendientes
       · rojo/rosa → ausencias (intensidad según estado)
       · gris      → selección local 
   – ★ Muestra el mensaje de error del backend cuando la solicitud
     de 5+ días no cumple la antelación mínima (2,5 meses).
   ────────────────────────────────────────────────────────────── */
import { useEffect, useMemo, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/es";
import "dayjs/locale/en";
import { useTranslation } from "react-i18next";
import { Tramo } from "../../hooks/useVacaciones";
import SelectTramoDialog from "../../components/SelectTramoDialog";
import { VacacionData } from "../../hooks/useVacaciones";
import { useTheme } from "@mui/material/styles";
import { DIAS_PER_COURSE } from "../../constants/enums";
import { useQuery } from "@tanstack/react-query";
/* ── MUI ─────────────────────────────────────────── */
import {
  Stack,
  Typography,
  CircularProgress,
  Box,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  MenuItem,
  Grid,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Divider,
  TableContainer,
} from "@mui/material";
import CircleIcon from "@mui/icons-material/Circle";
import {
  LocalizationProvider,
  DateCalendar,
  PickersDay,
  PickersDayProps,
  TimeField,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { ArrowBack, ArrowForward, Search } from "@mui/icons-material";

/* ── hooks / API ─────────────────────────────────── */
import { useAuth } from "../../context/AuthContext";
import { useVacaciones, IsoDate } from "../../hooks/useVacaciones";
import { useSolicitudesVacaciones } from "../../hooks/useSolicitudesVacaciones";
import { useAusencias, Ausencia } from "../../hooks/useAusencias";
import { useMotivosAusencia } from "../../hooks/useCalendario";
import api from "../../api/axios";

/* ── diálogo de ausencia (para ver / subir justificantes) ── */
import DlgAusenciaEdit from "../../components/DlgAusenciaEdit";

/* ══════════════════════════════════════════════════════════════
   Día con estilos dinámicos
   ══════════════════════════════════════════════════════════════ */
type StyledDayProps = PickersDayProps<Dayjs> & {
  tramo?: Tramo;
  approved?: boolean;
  pending?: boolean;
  aus?: {
    // datos mínimos que ya le pasábamos
    estado: "pending" | "approved" | "rejected";
    colorHex: string;
  };
  ausHalf?: boolean;
  ausCount?: number;
  selected?: boolean;
};

function StyledDay({
  tramo,
  approved,
  pending,
  aus,
  ausHalf,
  ausCount,
  selected,
  ...rest
}: StyledDayProps) {
  const theme = useTheme();

  /* 1 ▸ medio día vertical (vacaciones) */
  const halfVert = tramo === "manana" || tramo === "tarde";
  const isTop = tramo === "manana"; // «tarde» ⇒ mitad inferior

  /* 2 ▸ medio día diagonal (ausencia por horas) */
  const halfDiag = !!ausHalf;

  /* 3 ▸ colores base */
  const { bgColor, textColor } = (() => {
    /* vacaciones aprobadas */
    if (approved)
      return {
        bgColor: theme.palette.info.main,
        textColor: halfVert ? theme.palette.common.black : theme.palette.info.contrastText,
      };

    /* vacaciones pendientes */
    if (pending)
      return {
        bgColor: theme.palette.warning.main,
        textColor: halfVert ? theme.palette.common.black : theme.palette.warning.contrastText,
      };

    /* ausencias */
    if (aus) {
      const base = aus.estado === "pending" ? `${aus.colorHex}55` : aus.colorHex;
      return {
        bgColor: base,
        textColor: halfVert || halfDiag ? theme.palette.common.black : theme.palette.common.white,
      };
    }

    /* selección local */
    if (selected)
      return {
        bgColor: theme.palette.primary.light,
        textColor: theme.palette.primary.contrastText,
      };

    /* día vacío */
    return { bgColor: "transparent", textColor: theme.palette.text.primary };
  })();

  /* 4 ▸ render */
  return (
    <PickersDay
      {...rest}
      sx={{
        position: "relative",
        color: textColor,
        /* día completo → fondo sólido */
        ...(halfVert || halfDiag ? {} : { bgcolor: bgColor }),

        /* — medio día vertical — */
        ...(halfVert && {
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            borderRadius: "50%",
            backgroundColor: bgColor,
            clipPath: isTop
              ? "polygon(0 0,100% 0,100% 50%,0 50%)"
              : "polygon(0 50%,100% 50%,100% 100%,0 100%)",
            zIndex: -1,
            pointerEvents: "none",
          },
        }),

        /* — medio día diagonal (ausencia por horas) — */
        ...(halfDiag && {
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            borderRadius: "50%",
            backgroundImage: `linear-gradient(45deg, ${bgColor} 50%, transparent 50%)`,
            zIndex: -1,
            pointerEvents: "none",
          },
        }),
      }}
    >
      {/* 👇 nuevo badge contador */}
      {ausCount && ausCount > 1 && (
        <Box
          sx={{
            position: "absolute",
            bottom: 2,
            right: 2,
            bgcolor: "error.main",
            color: "common.white",
            fontSize: "0.65rem",
            px: 0.5,
            borderRadius: 1,
            lineHeight: 1.2,
          }}
        >
          +{ausCount - 1}
        </Box>
      )}
    </PickersDay>
  );
}

/* ══════════════════════════════════════════════════════════════
   Componente
   ══════════════════════════════════════════════════════════════ */
export default function VacacionesProfesorPage() {
  const { t, i18n } = useTranslation<"translation">();
  const { user } = useAuth();
  const uid = user?.id ?? 0;

  const [tramoDialogOpen, setTramoDialogOpen] = useState(false);
  const [selectedTramo, setSelectedTramo] = useState<Tramo>("completo");
  const [dlgAusList, setDlgAusList] = useState<{
    open: boolean;
    items: Ausencia[];
  }>({ open: false, items: [] });

  /* ─── datos ─────────────────────────────────────────────── */
  const { vacData: aprobadas, refresh: refVac, loading: loadVac } = useVacaciones(uid);
  const {
    rows: pendientes,
    refresh: refSol, // por si lo necesitas en otro sitio
    solicitar, // 👈 nuevo
  } = useSolicitudesVacaciones(uid);
  const { rows: ausRows, refresh: refAus } = useAusencias(uid);

  const mapaAusList = useMemo(() => {
    const m = new Map<string, Ausencia[]>();
    ausRows.forEach((a) => {
      if (!m.has(a.fecha)) m.set(a.fecha, []);
      m.get(a.fecha)!.push(a);
    });
    return m;
  }, [ausRows]);

  const { data: motivos = [] } = useMotivosAusencia();

  const mapaTramoAprob = useMemo(
    () => new Map(aprobadas.map((v) => [v.fecha, v.tramo])),
    [aprobadas]
  );
  const pendientesMapTramo = useMemo(
    () => new Map(pendientes.map((p) => [p.fecha, p.tramo])),
    [pendientes]
  );

  useEffect(() => {
    refVac();
    refSol();
    refAus();
  }, [refVac, refSol, refAus]);

  /* ─── helpers ───────────────────────────────────────────── */
  const setAprob = useMemo(() => new Set<IsoDate>(aprobadas.map((v) => v.fecha)), [aprobadas]);

  const setPend = useMemo(() => new Set<IsoDate>(pendientes.map((p) => p.fecha)), [pendientes]);

  const arrancarNuevaAusencia = (fecha: IsoDate) => {
    // si ya hay un bloqueo por ausencia día-completo, ni lo intentamos
    const hayCompleta =
      mapaAusList.get(fecha)?.some((a) => a.horaInicio == null && a.horaFin == null) ?? false;
    if (hayCompleta) return;

    setSelected(new Set([fecha]));
    setDlgAusList({ open: false, items: [] }); // cierra lista
    setDlgAus(true); // abre formulario “Nueva ausencia”
  };

  // ★ Hacer visible el error del backend si no se cumple la antelación mínima.
  const handleConfirmTramo = async (tramo: Tramo) => {
    const vacs = Array.from(selected).map((fecha) => ({ fecha, tramo }));

    // (opcional UX) Pre-chequeo rápido en el cliente para avisar antes de llamar
    const totalDias = vacs.reduce((acc, v) => acc + (v.tramo === "completo" ? 1 : 0.5), 0);
    if (totalDias >= 5) {
      const earliest = vacs.map((v) => v.fecha).reduce((min, f) => (f < min ? f : min));
      const minAllowed = dayjs().add(2, "month").add(15, "day");
      if (dayjs(earliest).isBefore(minAllowed, "day")) {
        alert(
          t(
            "profesorVacaciones.errorAntelacion",
            "Las solicitudes de 5 o más días deben realizarse con al menos 2 meses y medio de antelación."
          )
        );
        return;
      }
    }

    try {
      await solicitar(vacs); // ⬅️ pinta instantáneamente y refresca al llegar la respuesta
      setSelected(new Set());
      setTramoDialogOpen(false);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        t("profesorVacaciones.errorGenerico", "No se pudo crear la solicitud.");
      alert(msg);
      // en caso de optimismo previo, garantizamos estado coherente
      await refSol();
    }
  };

  /* ─── selección ─────────────────────────────────────────── */
  const [selected, setSelected] = useState<Set<IsoDate>>(new Set());
  const toggleSelect = (d: Dayjs) => {
    const key = d.format("YYYY-MM-DD") as IsoDate;
    const bloqueado =
      setAprob.has(key) ||
      setPend.has(key) ||
      (mapaAusList.get(key)?.some((a) => a.horaInicio == null && a.horaFin == null) ?? false);
    if (bloqueado) return;
    setSelected((p) => {
      const n = new Set(p);
      n.has(key) ? n.delete(key) : n.add(key);
      return n;
    });
  };

  /* ─── diálogo AUSENCIA (abrir desde calendario) ─────────── */
  const [dlgAbs, setDlgAbs] = useState<{
    open: boolean;
    abs?: Ausencia | null;
  }>({ open: false });
  const closeDlgAbs = () => setDlgAbs({ open: false });

  /* ─── curso ─────────────────────────────────────────────── */
  const today = dayjs();
  const initY = today.month() >= 8 ? today.year() : today.year() - 1;
  const [startY, setStartY] = useState(initY);
  const minDate = dayjs(`${startY}-09-01`);
  const maxDate = minDate.add(1, "year").subtract(1, "day");
  const months = useMemo(
    () => Array.from({ length: 12 }, (_, i) => minDate.add(i, "month")),
    [minDate]
  );

  /* ─── diálogo cancelar VACACIÓN ─────────────────────────── */
  const [dlgVac, setDlgVac] = useState<{
    open: boolean;
    id?: number;
    fecha?: IsoDate;
  }>({ open: false });
  const closeDlgVac = () => setDlgVac({ open: false });
  const cancelarSolicitud = async () => {
    if (!dlgVac.id) return;
    await api.delete(`/solicitudes-vacaciones/${dlgVac.id}`);
    await refSol();
    closeDlgVac();
  };

  /* ─── diálogo AUSENCIA (nuevo) ──────────────────────────── */
  const [dlgAus, setDlgAus] = useState(false);
  const [formAus, setFormAus] = useState<{
    motivoId: number | null;
    tramo: "dia" | "horas";
    horaInicio: Dayjs;
    horaFin: Dayjs;
    nota: string;
    file?: File;
  }>({
    motivoId: null,
    tramo: "dia",
    horaInicio: dayjs("09:00", "HH:mm"),
    horaFin: dayjs("13:00", "HH:mm"),
    nota: "",
  });

  const enviarAusencia = async () => {
    if (!formAus.motivoId) {
      alert("Motivo requerido");
      return;
    }

    // DTO base sin la fecha
    const dtoBase: any = {
      idProfesor: uid,
      motivoId: formAus.motivoId,
      tipo: "injustificada",
      nota: formAus.nota || null,
    };

    // Si es tramo de horas, añadimos horaInicio y horaFin
    if (formAus.tramo === "horas") {
      dtoBase.horaInicio = formAus.horaInicio.format("HH:mm");
      dtoBase.horaFin = formAus.horaFin.format("HH:mm");
    }

    // Cerramos el diálogo
    setDlgAus(false);

    try {
      // Recorremos cada fecha seleccionada
      for (const fecha of selected) {
        // 1) Creamos la solicitud de ausencia (estado = pending)
        const { data: ausencia } = await api.post<{ id: number }>("/ausencias", {
          ...dtoBase,
          fecha,
        });

        // 2) Si había un fichero adjunto, lo subimos al endpoint correcto
        if (formAus.file) {
          const formData = new FormData();
          formData.append("file", formAus.file);

          await api.post(`/ausencias/${ausencia.id}/adjuntos`, formData);
        }
      }

      // 3) Limpiamos selección y formulario
      setSelected(new Set());
      setFormAus((s) => ({ ...s, file: undefined }));

      // 4) Refrescamos datos: ausencias, solicitudes y vacaciones
      await refAus();
      await refSol();
      await refVac();
    } catch (err: any) {
      // Error al procesar ausencias
      const msg = err?.response?.data?.message || err?.message || "Error al enviar ausencia";
      alert(msg);
    }
  };

  /* ─── renderer día ───────────────────────────────────── */
  const dayRenderer = (p: PickersDayProps<Dayjs>) => {
    const str = p.day.format("YYYY-MM-DD") as IsoDate;

    const approved = setAprob.has(str);
    const pending = setPend.has(str);

    const ausList = mapaAusList.get(str); // ← NUEVO
    const aus = ausList?.[0]; // primer registro (p/ color)
    const ausHalf = ausList?.some((a) => a.horaInicio && a.horaFin) ?? false;
    const ausCount = ausList?.length ?? 0;

    const sel = selected.has(str);

    const tramoAprob = mapaTramoAprob.get(str);
    const tramoPend = pendientesMapTramo.get(str);

    const handleClickDia = (fecha: string, ausList: Ausencia[] | undefined, pendVac: boolean) => {
      if (ausList && ausList.length) {
        // SIEMPRE abrimos la lista (1 ó más ausencias)
        setDlgAusList({ open: true, items: ausList });
      } else if (pendVac) {
        const fila = pendientes.find((x) => x.fecha === fecha);
        if (fila) setDlgVac({ open: true, id: fila.id, fecha: fila.fecha });
      } else {
        toggleSelect(dayjs(fecha));
      }
    };

    return (
      <StyledDay
        {...p}
        tramo={tramoAprob ?? tramoPend}
        approved={approved}
        pending={pending}
        aus={aus ? { estado: aus.estado, colorHex: aus.motivo.colorHex } : undefined}
        ausHalf={ausHalf}
        ausCount={ausCount} // ← NUEVO
        selected={sel}
        onClick={() => handleClickDia(str, ausList, pending)}
      />
    );
  };

  /* ─── datos resumen ────────────────────────────────────── */
  const aprobadasOrden = useMemo(
    () => [...aprobadas].sort((a, b) => a.fecha.localeCompare(b.fecha)),
    [aprobadas]
  );
  const pendientesOrden = useMemo(
    () => [...pendientes].sort((a, b) => a.fecha.localeCompare(b.fecha)),
    [pendientes]
  );

  /* ── saldo que devuelve el backend ───────────────────── */
  interface ResumenItem {
    curso: string;
    asignados: number;
    disfrutados: number;
    saldo: number;
  }

  const {
    data: resumenData,
    isLoading: loadingRes,
    refetch: refRes,
  } = useQuery<ResumenItem[]>({
    queryKey: ["vac-resumen", uid],
    queryFn: async () => {
      const { data } = await api.get(`/vacaciones/resumen/${uid}`);
      return data as ResumenItem[];
    },
    enabled: !!uid, // no llames hasta tener UID
  });

  /* garantizamos siempre un array tipado */
  const resumen: ResumenItem[] = resumenData ?? [];

  /* ────── RESUMEN DE AUSENCIAS ────── */
  const ausResumen = useMemo(() => {
    const ini = `${startY}-09-01`;
    const fin = dayjs(ini).add(1, "year").subtract(1, "day");

    const mapa = new Map<number, { nombre: string; color: string; just: number; inj: number }>();

    ausRows
      .filter((a) => dayjs(a.fecha).isBetween(ini, fin, null, "[]"))
      .forEach((a) => {
        const id = a.motivo.id;
        if (!mapa.has(id))
          mapa.set(id, {
            nombre: a.motivo.nombreEs,
            color: a.motivo.colorHex,
            just: 0,
            inj: 0,
          });
        a.tipo === "justificada" ? mapa.get(id)!.just++ : mapa.get(id)!.inj++;
      });

    return [...mapa.values()].map((r) => ({ ...r, total: r.just + r.inj }));
  }, [ausRows, startY]);

  if (!user) return null;

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale={i18n.language.startsWith("en") ? "en" : "es"}
    >
      <Stack spacing={3}>
        <Grid container spacing={4}>
          {/* -------- Calendario (columna izquierda) -------- */}
          <Grid item xs={12} md={8} lg={9}>
            {/* título + curso */}
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="h5">{t("profesorVacaciones.title")}</Typography>
              <IconButton size="small" onClick={() => setStartY((y) => y - 1)}>
                <ArrowBack fontSize="small" />
              </IconButton>
              <Typography variant="h6">
                {startY}-{String(startY + 1).slice(2)}
              </Typography>
              <IconButton size="small" onClick={() => setStartY((y) => y + 1)}>
                <ArrowForward fontSize="small" />
              </IconButton>
            </Stack>

            {loadVac && <CircularProgress />}

            {!loadVac && (
              <>
                {/* calendarios */}
                <Stack direction="row" flexWrap="wrap" columnGap={4} rowGap={6}>
                  {months.map((m) => (
                    <Box key={m.format()} sx={{ width: 300 }}>
                      <Typography
                        variant="subtitle1"
                        align="center"
                        sx={{ mb: 1, textTransform: "capitalize" }}
                      >
                        {m.locale(i18n.language).format("MMMM")}
                      </Typography>

                      <DateCalendar
                        value={null}
                        referenceDate={m}
                        views={["day"]}
                        minDate={minDate}
                        maxDate={maxDate}
                        onChange={() => {}}
                        slots={{
                          day: dayRenderer,
                          calendarHeader: () => null,
                        }}
                        sx={{
                          ".MuiPickersCalendarHeader-root .MuiPickersArrowSwitcher-root": {
                            display: "none",
                          },
                          ".MuiPickersCalendarHeader-root": {
                            justifyContent: "center",
                          },
                          ".MuiDayCalendar-header": {
                            justifyContent: "center",
                          },
                        }}
                      />
                    </Box>
                  ))}
                </Stack>

                {/* botones */}
                <Stack direction="row" spacing={2} sx={{ alignSelf: "flex-start" }}>
                  <Button
                    variant="contained"
                    disabled={selected.size === 0}
                    onClick={() => setTramoDialogOpen(true)}
                  >
                    {t("profesorVacaciones.solicitar", {
                      count: selected.size,
                    })}
                  </Button>

                  <Button
                    variant="outlined"
                    color="secondary"
                    disabled={selected.size === 0}
                    onClick={() => setDlgAus(true)}
                  >
                    {t("absence.crear", { count: selected.size })}
                  </Button>
                </Stack>
              </>
            )}
          </Grid>
          {/* ───────────────────── Columna Derecha ───────────────────── */}
          <Grid
            item
            xs={12}
            md={4}
            lg={3}
            sx={{
              position: { lg: "sticky", sm: "static" },
              top: 80,
              zIndex: 2,
              bgcolor: "background.paper",
            }}
          >
            {/* ─────────── Saldo de vacaciones ─────────── */}
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
              {t("profesorVacaciones.resumen.titulo", "Saldo de vacaciones")}
            </Typography>

            <TableContainer
              component={Paper}
              sx={{
                width: "100%",
                tableLayout: { sm: "fixed", xs: "auto" },
                overflowX: { xs: "auto", sm: "hidden" },
                px: 1,
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ p: 0.5 }}>{t("profesorVacaciones.resumen.curso")}</TableCell>
                    <TableCell sx={{ p: 0.5 }} align="right">
                      {t("profesorVacaciones.resumen.asignados")}
                    </TableCell>
                    <TableCell sx={{ p: 0.5 }} align="right">
                      {t("profesorVacaciones.resumen.disfrutados")}
                    </TableCell>
                    <TableCell sx={{ p: 0.5 }} align="right">
                      {t("profesorVacaciones.resumen.saldo")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingRes ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <CircularProgress size={20} />
                      </TableCell>
                    </TableRow>
                  ) : resumen.length ? (
                    resumen.map((r: ResumenItem) => (
                      <TableRow key={r.curso}>
                        <TableCell sx={{ p: 0.5 }}>{r.curso}</TableCell>
                        <TableCell sx={{ p: 0.5 }} align="right">
                          {r.asignados}
                        </TableCell>
                        <TableCell sx={{ p: 0.5 }} align="right">
                          {r.disfrutados}
                        </TableCell>
                        <TableCell
                          sx={{
                            p: 0.5,
                            color: r.saldo < 0 ? "error.main" : "success.main",
                          }}
                          align="right"
                        >
                          {r.saldo}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        {t("profesorVacaciones.resumen.vacio")}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* ───────────── Ausencias ───────────── */}
            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
              {t("profesorVacaciones.ausencias.titulo", "Ausencias")}
            </Typography>

            <TableContainer
              component={Paper}
              sx={{
                width: "100%",
                tableLayout: { sm: "fixed", xs: "auto" },
                overflowX: { xs: "auto", sm: "hidden" },
                px: 1,
                mt: 1.5,
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ p: 0.5 }}>Motivo</TableCell>
                    <TableCell sx={{ p: 0.5 }} align="right">
                      Just.
                    </TableCell>
                    <TableCell sx={{ p: 0.5 }} align="right">
                      Injust.
                    </TableCell>
                    <TableCell sx={{ p: 0.5 }} align="right">
                      Total
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ausResumen.length ? (
                    ausResumen.map((r) => (
                      <TableRow key={r.nombre}>
                        <TableCell sx={{ p: 0.5 }}>
                          <CircleIcon fontSize="small" sx={{ color: r.color, mr: 0.75 }} />
                          {r.nombre}
                        </TableCell>
                        <TableCell sx={{ p: 0.5 }} align="right">
                          {r.just}
                        </TableCell>
                        <TableCell sx={{ p: 0.5 }} align="right">
                          {r.inj}
                        </TableCell>
                        <TableCell sx={{ p: 0.5 }} align="right">
                          {r.total}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell sx={{ p: 0.5 }} colSpan={4} align="center">
                        {t("profesorVacaciones.ausencias.vacio", "Sin ausencias")}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>{" "}
          {/* ← cierra la columna-derecha */}
        </Grid>{" "}
        {/* ← cierra el <Grid container …> */}
      </Stack>

      {/* cancelar VACACIÓN */}
      <Dialog open={dlgVac.open} onClose={closeDlgVac}>
        <DialogTitle>{t("profesorVacaciones.dlg.titulo")}</DialogTitle>
        <DialogContent>
          <Typography>{t("profesorVacaciones.dlg.msg", { fecha: dlgVac.fecha })}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDlgVac}>{t("common.cancel")}</Button>
          <Button color="error" variant="contained" onClick={cancelarSolicitud}>
            {t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* nueva AUSENCIA */}
      <Dialog open={dlgAus} onClose={() => setDlgAus(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("absence.nueva")}</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <TextField
            select
            fullWidth
            margin="normal"
            label={t("absence.motivo")}
            value={formAus.motivoId ?? ""}
            onChange={(e) =>
              setFormAus((s) => ({
                ...s,
                motivoId: Number(e.target.value) || null,
              }))
            }
          >
            {motivos.map((m: { id: number; nombreEs: string }) => (
              <MenuItem key={m.id} value={m.id}>
                {m.nombreEs}
              </MenuItem>
            ))}
          </TextField>

          <RadioGroup
            row
            value={formAus.tramo}
            onChange={(_, v) => setFormAus((s) => ({ ...s, tramo: v as any }))}
            sx={{ mb: 2 }}
          >
            <FormControlLabel value="dia" control={<Radio />} label={t("absence.diaCompleto")} />
            <FormControlLabel value="horas" control={<Radio />} label={t("absence.horas")} />
          </RadioGroup>

          {formAus.tramo === "horas" && (
            <Stack direction="row" spacing={2}>
              <TimeField
                label={t("absence.inicio")}
                value={formAus.horaInicio}
                onChange={(v) => setFormAus((s) => ({ ...s, horaInicio: v! }))}
              />
              <TimeField
                label={t("absence.fin")}
                value={formAus.horaFin}
                onChange={(v) => setFormAus((s) => ({ ...s, horaFin: v! }))}
              />
            </Stack>
          )}

          <TextField
            fullWidth
            margin="normal"
            multiline
            minRows={2}
            label={t("absence.nota")}
            value={formAus.nota}
            onChange={(e) => setFormAus((s) => ({ ...s, nota: e.target.value }))}
          />

          <Button component="label" sx={{ mt: 1 }}>
            {t("absence.subirAdjunto")}
            <input
              hidden
              type="file"
              onChange={(e) => setFormAus((s) => ({ ...s, file: e.target.files?.[0] }))}
            />
          </Button>
          {formAus.file && (
            <Typography variant="caption" sx={{ ml: 1 }}>
              {formAus.file?.name}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgAus(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={enviarAusencia}>
            {t("common.send")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ver AUSENCIA existente (profesor) */}
      <DlgAusenciaEdit
        open={dlgAbs.open}
        absence={dlgAbs.abs ?? null}
        onClose={closeDlgAbs}
        onSaved={async () => {
          await refAus();
        }}
        canApprove={false} /* 👈 oculta campos HR */
      />
      <SelectTramoDialog
        open={tramoDialogOpen}
        initial={selectedTramo}
        onCancel={() => setTramoDialogOpen(false)}
        onConfirm={handleConfirmTramo}
      />
      {/* ─── lista de AUSENCIAS del día ─────────────────────────── */}
      <Dialog
        open={dlgAusList.open}
        onClose={() => setDlgAusList({ open: false, items: [] })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("absence.listaDelDia", "Ausencias del día")}</DialogTitle>

        <DialogContent dividers>
          {dlgAusList.items.map((a) => (
            <Box
              key={a.id}
              sx={{
                p: 1,
                mb: 1,
                borderRadius: 1,
                bgcolor: "action.hover",
                cursor: "pointer",
              }}
              onClick={() => {
                setDlgAbs({ open: true, abs: a });
                setDlgAusList({ open: false, items: [] });
              }}
            >
              <Typography variant="subtitle2">
                {a.horaInicio && a.horaFin
                  ? `${a.horaInicio.slice(0, 5)}–${a.horaFin.slice(0, 5)}`
                  : t("absence.diaCompleto")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {a.motivo.nombreEs}
              </Typography>
            </Box>
          ))}
        </DialogContent>

        <DialogActions>
          {/* botón Cerrar */}
          <Button onClick={() => setDlgAusList({ open: false, items: [] })}>
            {t("common.close")}
          </Button>

          {/* botón “Nueva ausencia” (sólo si NO hay ausencia de día completo) */}
          {!dlgAusList.items.some((a) => a.horaInicio == null && a.horaFin == null) && (
            <Button
              variant="contained"
              onClick={() => arrancarNuevaAusencia(dlgAusList.items[0].fecha as IsoDate)}
            >
              {t("absence.nueva")}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
}
